import React, { useEffect, useState, useCallback } from "react";
import { X, RotateCcw, Trash2 } from "lucide-react";
import api, { formatApiError, money, clientLabel } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ItemsEditor } from "./ItemsEditor";
import { SearchableSelect } from "./SearchableSelect";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { useFormDraft } from "../hooks/useFormDraft";
import { toast } from "sonner";

const emptyItem = { product_id: "", code: "", description: "", unit: "Unidad", qty: 1, unit_price: 0, discount_pct: 0, observation: "" };
const emptyForm = { client_id: "", contact_id: "", manual_client: false, manual_client_name: "", currency: "GTQ", exchange_rate: 1, payment_condition: "", notes: "", delivery_address: "", carrier: "", follow_up_days: 3, items: [] };
const paymentDefault = (c) => { const d = Number(c?.credit_days || 0); return d <= 0 ? "Contado" : `Crédito ${d} días`; };

export const DirectDocDialog = ({ open, onOpenChange, kind, onSaved, doc = null }) => {
  const isDelivery = kind === "delivery";
  const isEdit = !!doc;
  const label = isDelivery ? "nota de envío" : "pedido";
  const [syncAsk, setSyncAsk] = useState(false);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [prodSearch, setProdSearch] = useState("");
  const [prodResults, setProdResults] = useState([]);
  const [saving, setSaving] = useState(false);

  const isDocEmpty = useCallback((f) => !f.client_id && (f.items?.length || 0) === 0 && !f.notes, []);
  const { draftAvailable, loadDraft, clearDraft } = useFormDraft({ storageKey: `selec_draft_${kind}`, enabled: open && !isEdit, value: form, isEmpty: isDocEmpty });
  const restoreDraft = async () => {
    const d = loadDraft(); if (!d) return;
    setForm({ ...emptyForm, ...d });
    setContacts([]);
    if (d.client_id) { try { const r = await api.get(`/clients/${d.client_id}`); setContacts(r.data.contacts || []); } catch {} }
    clearDraft();
  };

  useEffect(() => {
    if (!open) { return; }
    setProdSearch(""); setProdResults([]); setContacts([]);
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    if (doc) {
      setForm({ ...emptyForm, client_id: doc.client_id || "", manual_client: !doc.client_id, manual_client_name: doc.client_id ? "" : (doc.client_name || ""),
        contact_id: doc.contact_id || "", currency: doc.currency || "GTQ", exchange_rate: doc.exchange_rate || 1, payment_condition: doc.payment_condition || "",
        notes: doc.notes || "", delivery_address: doc.delivery_address || "", carrier: doc.carrier || "", follow_up_days: 0,
        items: (doc.items || []).map((it) => ({ ...emptyItem, ...it })) });
      if (doc.client_id) api.get(`/clients/${doc.client_id}`).then((r) => setContacts(r.data.contacts || [])).catch(() => {});
    } else setForm(emptyForm);
  }, [open, doc]);

  useEffect(() => {
    if (!prodSearch) { setProdResults([]); return; }
    const t = setTimeout(() => api.get(`/products?q=${encodeURIComponent(prodSearch)}`).then((r) => setProdResults(r.data.slice(0, 8))).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [prodSearch]);

  const selectClient = async (client_id) => {
    const client = clients.find((c) => c.id === client_id);
    setForm((f) => ({ ...f, client_id, contact_id: "", payment_condition: paymentDefault(client) }));
    setContacts([]);
    if (client_id) { try { const r = await api.get(`/clients/${client_id}`); setContacts(r.data.contacts || []); } catch {} }
  };
  const addProduct = (p) => { setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem, product_id: p.id, code: p.code, description: p.description, unit: p.unit || "Unidad", unit_price: p.price || 0 }] })); setProdSearch(""); setProdResults([]); };
  const addBlank = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const updItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));
  const rmItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const askClose = () => form.items.length === 0 || window.confirm("Tienes ítems sin guardar. ¿Seguro que deseas cerrar? Se perderá lo capturado.");

  const subtotal = form.items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount_pct || 0) / 100), 0);

  const save = async (sync) => {
    if (form.manual_client) { if (!form.manual_client_name.trim()) return toast.error("Escribe el nombre del cliente"); }
    else if (!form.client_id) return toast.error("Selecciona un cliente");
    if (form.items.length === 0) return toast.error("Agrega al menos una línea");
    if (isEdit && doc.quotation_id && typeof sync !== "boolean") return setSyncAsk(true);
    setSyncAsk(false);
    setSaving(true);
    try {
      const payload = form.manual_client ? { ...form, client_id: "", contact_id: "" } : form;
      if (isEdit) {
        await api.put(`/deliveries/${doc.id}/full`, { ...payload, sync_quote: sync === true });
        toast.success(`Nota de envío ${doc.number} actualizada`);
      } else {
        const r = await api.post(isDelivery ? "/deliveries" : "/orders", payload);
        toast.success(`${isDelivery ? "Nota de envío" : "Pedido"} ${r.data.number} creado`);
        clearDraft();
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { if (askClose()) onOpenChange(false); } else onOpenChange(true); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`direct-${kind}-dialog`}
        onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? `Editar ${label} ${doc.number}` : `Nuevo/a ${label}`}</DialogTitle>
          <DialogDescription>{isEdit ? (doc.quotation_number ? `Ligada a la cotización ${doc.quotation_number}.` : "Edita cliente, productos, cantidades y precios.") : "Crea directamente sin partir de una cotización."}</DialogDescription>
        </DialogHeader>
        {draftAvailable && (
          <div className="flex items-center justify-between gap-3 rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm" data-testid={`${kind}-draft-banner`}>
            <span className="text-amber-600">Tienes un borrador sin guardar de un/a {label} anterior.</span>
            <div className="flex gap-2 shrink-0">
              <Button type="button" size="sm" variant="outline" className="rounded-sm h-8 gap-1 border-amber-500/40 text-amber-600" onClick={restoreDraft} data-testid={`${kind}-draft-restore`}><RotateCcw className="h-3.5 w-3.5" /> Recuperar</Button>
              <Button type="button" size="sm" variant="ghost" className="rounded-sm h-8 gap-1 text-muted-foreground" onClick={clearDraft} data-testid={`${kind}-draft-discard`}><Trash2 className="h-3.5 w-3.5" /> Descartar</Button>
            </div>
          </div>
        )}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-widest">Cliente *</Label>
                {isDelivery && (
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer" data-testid="delivery-manual-client-toggle">
                    <input type="checkbox" checked={form.manual_client} onChange={(e) => setForm((f) => ({ ...f, manual_client: e.target.checked, client_id: "", contact_id: "" }))} />
                    Cliente no registrado
                  </label>
                )}
              </div>
              {form.manual_client ? (
                <Input className="rounded-sm" placeholder="Nombre del cliente (manual)" value={form.manual_client_name} onChange={(e) => setForm((f) => ({ ...f, manual_client_name: e.target.value }))} data-testid={`${kind}-manual-client-name`} />
              ) : (
                <SearchableSelect value={form.client_id} onChange={selectClient} testid={`${kind}-client-select`}
                  placeholder="Selecciona un cliente" searchPlaceholder="Escribe para buscar cliente..."
                  options={clients.map((c) => ({ value: c.id, label: clientLabel(c) }))} />
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Contacto</Label>
              <Select value={form.contact_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, contact_id: v === "none" ? "" : v }))} disabled={form.manual_client}>
                <SelectTrigger className="rounded-sm" data-testid={`${kind}-contact-select`}><SelectValue placeholder="Sin contacto" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Sin contacto</SelectItem>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.last_name || ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Moneda</Label>
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger className="rounded-sm" data-testid={`${kind}-currency-select`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="GTQ">GTQ (Quetzales)</SelectItem><SelectItem value="USD">USD (Dólares)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Condición de pago</Label>
              <Input className="rounded-sm" placeholder="Contado / Crédito X días" value={form.payment_condition} onChange={(e) => setForm((f) => ({ ...f, payment_condition: e.target.value }))} data-testid={`${kind}-payment-condition`} />
            </div>
          </div>

          {isDelivery && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Dirección de entrega</Label>
                <Input className="rounded-sm" value={form.delivery_address} onChange={(e) => setForm((f) => ({ ...f, delivery_address: e.target.value }))} data-testid="delivery-address-new" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Transportista / Mensajero</Label>
                <Input className="rounded-sm" value={form.carrier} onChange={(e) => setForm((f) => ({ ...f, carrier: e.target.value }))} data-testid="delivery-carrier-new" /></div>
              {!isEdit && <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Días para seguimiento</Label>
                <Input type="number" min="0" className="rounded-sm" value={form.follow_up_days} onChange={(e) => setForm((f) => ({ ...f, follow_up_days: parseInt(e.target.value, 10) || 0 }))} data-testid="delivery-follow-days" /><p className="text-[10px] text-muted-foreground">Agenda un seguimiento en la agenda comercial. 0 = ninguno.</p></div>}
            </div>
          )}

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Ítems</Label>
            <div className="mt-1.5">
              <ItemsEditor items={form.items} onChange={(items) => setForm((f) => ({ ...f, items }))} currency={form.currency}
                priceKey="unit_price" priceLabel="Precio" showDiscount={false} showObservation testidPrefix={kind}
                productMap={(p) => ({ product_id: p.id, code: p.code, description: p.description, unit: p.unit || "Unidad", unit_price: p.price || 0 })} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex-1"><Textarea placeholder="Observaciones" className="rounded-sm" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
            <div className="w-full sm:w-64 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono" data-testid={`${kind}-subtotal`}>{money(form.currency, subtotal)}</span></div>
              <div className="text-xs text-muted-foreground italic">Precios con IVA incluido</div>
              <div className="flex justify-between border-t border-border pt-2 text-base font-bold text-primary"><span>TOTAL</span><span className="font-mono" data-testid={`${kind}-total`}>{money(form.currency, subtotal)}</span></div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => { if (askClose()) onOpenChange(false); }}>Cancelar</Button>
          <Button className="rounded-sm font-bold" onClick={save} disabled={saving} data-testid={`save-${kind}-btn`}>Guardar</Button>
        </DialogFooter>
        <Dialog open={syncAsk} onOpenChange={setSyncAsk}>
          <DialogContent className="rounded-sm max-w-md" data-testid="delivery-sync-dialog">
            <DialogHeader><DialogTitle className="font-heading">¿Modificar también la cotización?</DialogTitle>
              <DialogDescription>Esta nota está ligada a la cotización <strong>{doc?.quotation_number}</strong>. Puedes aplicar los mismos cambios a la cotización o guardar solo la nota de envío (lo que falte enviar queda pendiente en la cotización).</DialogDescription></DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" disabled={saving} onClick={() => save(false)} data-testid="delivery-sync-no">Solo la nota de envío</Button>
              <Button className="rounded-sm font-bold" disabled={saving} onClick={() => save(true)} data-testid="delivery-sync-yes">Sí, modificar la cotización</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
